import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { OptionField } from 'components/Field/OptionField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import {
  doEngageFlatsLoad,
  doEngageFlatsSetFilter,
} from 'store/remote/engageActions';
import {
  selectEngageFlatsFilterIssueContactTypes,
  selectEngageFlatsFilterIsCommerciallyOwned,
  selectEngageFlatsFilterIsHMO,
  selectEngageFlatsFilterOccupationStatuses,
  selectEngageFlatsRequiresResidentSupport,
} from 'store/remote/engageSelectors';
import {
  EngageFlatIsCommerciallyOwned,
  EngageFlatIsHMO,
  FlatIssueType,
  FlatRequiresResidentSupport,
} from 'types/DTOs/BlockManagerDTOs';
import {
  EngageFlatsCommerciallyOwnedFilters,
  EngageFlatsDefaultFilter,
  EngageFlatsHMOFilters,
  EngageFlatsIssueTypeFilters,
  EngageFlatsOccupationStatusFilters,
  EngageFlatsRequiresResidentSupportFilters,
  FlatOccupationStatus,
} from 'types/Engage';

export const EngageFlatsFilterModal: React.FC<
  ModalScreenProps<ModalScreens.EngageFlatsFilter>
> = ({ route }) => {
  const dispatch = useDispatch();
  const { pageKey, propertyId } = route.params;

  const currentOccupationStatuses = useSelector((state) =>
    selectEngageFlatsFilterOccupationStatuses(state, pageKey),
  );
  const currentissueContactTypes = useSelector((state) =>
    selectEngageFlatsFilterIssueContactTypes(state, pageKey),
  );
  const currentRequiresResidentSupport = useSelector((state) =>
    selectEngageFlatsRequiresResidentSupport(state, pageKey),
  );
  const currentIsHMO = useSelector((state) =>
    selectEngageFlatsFilterIsHMO(state, pageKey),
  );
  const currentIsCommerciallyOwned = useSelector((state) =>
    selectEngageFlatsFilterIsCommerciallyOwned(state, pageKey),
  );

  const [occupationStatuses, setOccupationStatuses] = React.useState(
    currentOccupationStatuses,
  );

  const [issueContactTypes, setissueContactTypes] = React.useState(
    currentissueContactTypes,
  );

  const [requiresResidentSupport, setRequiresResidentSupport] = React.useState(
    currentRequiresResidentSupport,
  );
  const [isHMO, setIsHMO] = React.useState(currentIsHMO);
  const [isCommerciallyOwned, setIsCommerciallyOwned] = React.useState(
    currentIsCommerciallyOwned,
  );

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  const handleApply = React.useCallback(() => {
    dispatch(
      doEngageFlatsSetFilter({
        key: pageKey,
        data: {
          occupationStatuses,
          issueContactTypes,
          requiresResidentSupport,
          isHMO,
          isCommerciallyOwned,
        },
      }),
    );
    dispatch(
      doEngageFlatsLoad({
        key: pageKey,
        propertyId,
        page: 'reset',
      }),
    );
    handleClose();
  }, [
    dispatch,
    handleClose,
    occupationStatuses,
    issueContactTypes,
    requiresResidentSupport,
    isHMO,
    isCommerciallyOwned,
    pageKey,
    propertyId,
  ]);

  const handleClear = React.useCallback(() => {
    setOccupationStatuses(EngageFlatsDefaultFilter.occupationStatuses);
    setissueContactTypes(EngageFlatsDefaultFilter.issueContactTypes);
    setRequiresResidentSupport(
      EngageFlatsDefaultFilter.requiresResidentSupport,
    );
    setIsHMO(EngageFlatsDefaultFilter.isHMO);
    setIsCommerciallyOwned(EngageFlatsDefaultFilter.isCommerciallyOwned);
  }, []);

  const handleOccupationStatusChange = React.useCallback((values: string[]) => {
    setOccupationStatuses(values as FlatOccupationStatus[]);
  }, []);

  const handleIssueContactTypesChange = React.useCallback(
    (values: FlatIssueType[]) => {
      setissueContactTypes(values);
    },
    [],
  );

  const handleRequiresResidentSupportChange = React.useCallback(
    (values: FlatRequiresResidentSupport[]) => {
      setRequiresResidentSupport(values);
    },
    [],
  );

  const handleIsHMOChange = React.useCallback((values: EngageFlatIsHMO[]) => {
    setIsHMO(values);
  }, []);

  const handleIsCommerciallyOwnedChange = React.useCallback(
    (values: EngageFlatIsCommerciallyOwned[]) => {
      setIsCommerciallyOwned(values);
    },
    [],
  );

  const occupationBadgeActive = occupationStatuses.length !== 0;

  const issueContactTypesBadgeActive = issueContactTypes.length !== 0;

  const requiresResidentSupportBadgeActive =
    requiresResidentSupport.length !== 0;

  const isHMOBadgeActive = isHMO.length !== 0;
  const isCommerciallyOwnedBadgeActive = isCommerciallyOwned.length !== 0;

  const isDefaultSelection =
    isEqual(occupationStatuses, EngageFlatsDefaultFilter.occupationStatuses) &&
    isEqual(issueContactTypes, EngageFlatsDefaultFilter.issueContactTypes) &&
    isEqual(
      requiresResidentSupport,
      EngageFlatsDefaultFilter.requiresResidentSupport,
    ) &&
    isEqual(isHMO, EngageFlatsDefaultFilter.isHMO) &&
    isEqual(isCommerciallyOwned, EngageFlatsDefaultFilter.isCommerciallyOwned);

  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Filter"
          subtitle="Flats"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleApply}
        />
      }
    >
      <Card>
        <OptionField
          label="Occupation Status"
          modalLabel="Occupation Status"
          onChange={handleOccupationStatusChange}
          value={occupationStatuses}
          multiSelect
          modalEnableSelectAll
          showBadge={occupationBadgeActive}
          options={EngageFlatsOccupationStatusFilters.map((status) => ({
            name: status.name,
            internalValue: status.id,
          }))}
          showLabelInCloseHeader
          labelAlignTop
          expandedInlineLast
          modalSize="small"
          showFieldTypeIcon={false}
          modalShowQuickChoose
        />
        <OptionField
          label="Issue Types"
          modalLabel="Issue Types"
          onChange={handleIssueContactTypesChange}
          value={issueContactTypes}
          multiSelect
          modalEnableSelectAll
          showBadge={issueContactTypesBadgeActive}
          options={EngageFlatsIssueTypeFilters.map((status) => ({
            name: status.name,
            internalValue: status.id,
          }))}
          showLabelInCloseHeader
          labelAlignTop
          expandedInlineLast
          modalSize="small"
          showFieldTypeIcon={false}
          modalShowQuickChoose
        />
        <OptionField
          label="Requires Resident Support"
          modalLabel="Requires Resident Support"
          onChange={handleRequiresResidentSupportChange}
          value={requiresResidentSupport}
          multiSelect
          modalEnableSelectAll
          showBadge={requiresResidentSupportBadgeActive}
          options={EngageFlatsRequiresResidentSupportFilters.map((status) => ({
            name: status.name,
            internalValue: status.id,
          }))}
          showLabelInCloseHeader
          labelAlignTop
          expandedInlineLast
          modalSize="small"
          showFieldTypeIcon={false}
          modalShowQuickChoose
        />
        <OptionField
          label="HMO"
          modalLabel="HMO"
          onChange={handleIsHMOChange}
          value={isHMO}
          multiSelect
          modalEnableSelectAll
          showBadge={isHMOBadgeActive}
          options={EngageFlatsHMOFilters.map((status) => ({
            name: status.name,
            internalValue: status.id,
          }))}
          showLabelInCloseHeader
          labelAlignTop
          expandedInlineLast
          modalSize="small"
          showFieldTypeIcon={false}
          modalShowQuickChoose
        />
        <OptionField
          label="Institutional Landlord"
          modalLabel="Institutional Landlord"
          onChange={handleIsCommerciallyOwnedChange}
          value={isCommerciallyOwned}
          multiSelect
          modalEnableSelectAll
          showBadge={isCommerciallyOwnedBadgeActive}
          options={EngageFlatsCommerciallyOwnedFilters.map((status) => ({
            name: status.name,
            internalValue: status.id,
          }))}
          showLabelInCloseHeader
          labelAlignTop
          expandedInlineLast
          modalSize="small"
          showFieldTypeIcon={false}
          modalShowQuickChoose
        />
      </Card>

      <Button
        text="Clear Filters"
        style={{
          alignSelf: 'center',
        }}
        onPress={handleClear}
        disabled={isDefaultSelection}
      />
    </Page>
  );
};
